// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { get } from '@tls/auth';
import { ProjectMetaMessage } from '@tls/sw91-communication/types/com.base';
import { Dictionary } from 'model/Dictionary';
import { ProjectStatus } from 'model/ProjectMetadata';
import { ProjectStatusAckMessage } from 'model/reponse/ProjectStatusAckMessage';
import { loadStoreStateFromLocalStorage } from 'store/rehydrate';

export interface ProjectsState {
  projectsMetadata: ProjectMetaMessage[];
  loading?: boolean;
  lastStatus?: number;
  status: Dictionary<ProjectStatus>;
  lockedBy: Dictionary<string>;
  isInitializing: boolean;
  readyProjectIds: string[];
}

export type InitializationStatusDto = {
  isInitializing?: boolean;
};

type ProjectsFetchState = {
  jsonData: unknown[];
  lastStatus?: number;
  status: Dictionary<ProjectStatus>;
  lockedBy: Dictionary<string>;
  readyProjectIds: string[];
  isInitializing: boolean;
};

const initialState: ProjectsState = {
  ...loadStoreStateFromLocalStorage(l => l?.projects, {
    tags: [],
    projectsMetadata: [],
    status: {},
    jsonData: [],
    lockedBy: {},
  } as unknown as ProjectsState),
  // Live state of the DbAccess startup project-initialization. Deliberately NOT restored from
  // localStorage so a page reload never shows a stale "loading" state — the ready set is
  // re-derived from the server (GET /api/projects) on every load.
  isInitializing: false,
  readyProjectIds: [],
};

const fetchProjects = createAsyncThunk<ProjectsFetchState, undefined, { rejectValue: ProjectsFetchState }>(
  'fetch-projects',
  async (_, thunkApi) => {
    const getAwaiter = get('projects');
    const statusAwaiter = get('projects/status');
    const lockAwaiter = get('projects/lockStatus');
    const initStatusAwaiter = get('projects/initStatus');

    const { data, status } = await getAwaiter;
    const { data: statusData } = await statusAwaiter;
    const { data: lockStatusData } = await lockAwaiter;
    const { data: initStatusData } = await initStatusAwaiter;

    const resultData: ProjectsFetchState = {
      lastStatus: status,
      status: {},
      jsonData: [],
      lockedBy: {},
      readyProjectIds: [],
      isInitializing: false,
    };

    if (status < 200 || status >= 400) {
      return thunkApi.rejectWithValue(resultData);
    }

    if (statusData && typeof statusData === 'object') {
      resultData.status = statusData;
    }

    if (lockStatusData && typeof lockStatusData === 'object') {
      resultData.lockedBy = lockStatusData;
    }

    if (initStatusData && typeof initStatusData === 'object') {
      resultData.isInitializing = !!(initStatusData as InitializationStatusDto).isInitializing;
    }

    const projects = Array.isArray(data) ? data : [];
    resultData.jsonData = projects;
    resultData.readyProjectIds = projects
      .map(d => ProjectMetaMessage.fromJSON(d).projectId)
      .filter((id): id is string => !!id);
    return resultData;
  }
);

// Seeds the banner state (used by the app-wide banner before the project list is fetched).
const fetchInitializationStatus = createAsyncThunk('fetch-initialization-status', async () => {
  const { data, status } = await get('projects/initStatus');
  if (status < 200 || status >= 400 || !data || typeof data !== 'object') {
    return undefined;
  }
  return data as InitializationStatusDto;
});

export const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    updateProjectStatus: (state, action: PayloadAction<ProjectStatusAckMessage>) => {
      const { projectId, status } = action.payload;
      if (!projectId) return;
      state.status[projectId] = status;
    },
    // Drops a project locally, e.g. after the user removed their own access
    removeProject: (state, action: PayloadAction<string>) => {
      const projectId = action.payload;
      state.projectsMetadata = state.projectsMetadata.filter(p => p.projectId !== projectId);
      state.readyProjectIds = state.readyProjectIds.filter(id => id !== projectId);
      delete state.status[projectId];
      delete state.lockedBy[projectId];
    },
    initializationStarted: state => {
      state.isInitializing = true;
    },
    initializationCompleted: state => {
      state.isInitializing = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchProjects.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchProjects.rejected, (state, action) => {
      state.loading = false;
      state.lastStatus = action.payload?.lastStatus ?? 200;
    });
    builder.addCase(fetchInitializationStatus.fulfilled, (state, action) => {
      if (!action.payload) return;
      state.isInitializing = action.payload.isInitializing ?? false;
    });
    builder.addCase(fetchProjects.fulfilled, (state, action) => {
      state.loading = false;
      state.lastStatus = action.payload.lastStatus;
      state.status = action.payload.status ?? {};
      state.lockedBy = action.payload.lockedBy ?? {};
      // GET /api/projects returns exactly the initialized projects, so its ids ARE the ready set.
      // Re-fetched on every load, this stays correct across reloads without persisting the set.
      state.readyProjectIds = action.payload.readyProjectIds;
      state.isInitializing = action.payload.isInitializing;

      const fresh = action.payload.jsonData.map(ProjectMetaMessage.fromJSON);
      if (action.payload.isInitializing) {
        for (const meta of fresh) {
          if (!meta.projectId) continue;
          const index = state.projectsMetadata.findIndex(p => p.projectId === meta.projectId);
          if (index >= 0) {
            state.projectsMetadata[index] = meta;
          } else {
            state.projectsMetadata.push(meta);
          }
        }
      } else {
        // Initialization done
        state.projectsMetadata = fresh;
      }
    });
  },
});

export const { updateProjectStatus, removeProject, initializationStarted, initializationCompleted } =
  projectsSlice.actions;
export { fetchProjects, fetchInitializationStatus };
export default projectsSlice.reducer;
